<template>
  <li :class="['task-item', { completed: stepSeconds > 0 }]">
    <div class="text">{{ text }}</div>
    <div v-if="hasPills" class="attr-pills">
      <span v-if="isNegative" class="attr-pill negative">negative</span>
      <span v-else-if="isOptional" class="attr-pill optional">optional</span>
      <span v-if="isLowConfidence" class="attr-pill low-confidence">
        low confidence
      </span>
    </div>
    <div v-if="showMetrics" class="metrics-container">
      <span class="metric-pill va">
        <span class="metric-label">VA</span>
        <span class="metric-value">{{ formatTime(displayMetrics.vaTime) }}</span>
      </span>
      <span class="metric-pill nva">
        <span class="metric-label">NVA</span>
        <span class="metric-value">{{ formatTime(displayMetrics.nvaTime) }}</span>
      </span>
      <span class="metric-pill total">
        <span class="metric-label">Total</span>
        <span class="metric-value">{{ formatTime(displayMetrics.totalTime) }}</span>
      </span>
    </div>
    <div v-else class="step-time">{{ stepTime }}</div>
  </li>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'TaskItem',
  props: {
    text: { type: String, required: true },
    substepIndex: { type: Number, required: true },
    stepsTime: { type: Object, default: () => ({}) },
    substepMetrics: { type: Object, default: () => ({}) },
    isOptional: { type: Boolean, default: false },
    isNegative: { type: Boolean, default: false },
    isLowConfidence: { type: Boolean, default: false },
  },
  setup(props) {
    const stepSeconds = computed(() =>
      Number(props.stepsTime?.[props.substepIndex] ?? 0)
    );
    const stepTime = computed(() => `${stepSeconds.value.toFixed(2)} s`);

    const metrics = computed(() => props.substepMetrics?.[props.substepIndex]);

    const displayMetrics = computed(() => {
      if (metrics.value) return metrics.value;
      const time = stepSeconds.value;
      return { vaTime: time, nvaTime: 0, totalTime: time };
    });

    const showMetrics = computed(() => {
      return localStorage.getItem('organization') === 'bmwgermanye5cmzyxzt6bi9rso6';
    });

    const formatTime = (time) => {
      return `${Number(time || 0).toFixed(2)} s`;
    };

    const hasPills = computed(
      () => props.isNegative || props.isOptional || props.isLowConfidence
    );

    return {
      stepSeconds,
      stepTime,
      displayMetrics,
      showMetrics,
      formatTime,
      hasPills,
    };
  },
};
</script>

<style scoped>
.task-item {
  display: flex;
  gap: 0.5em;
  justify-content: space-between;
  align-items: center; /* pill + time vertically centered against the wrapped name */
  flex-wrap: nowrap; /* keep name and time on one row */
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  padding: 8px 10px;
}

.completed {
  background: #f0fdf4;
}

.text {
  font-size: var(--fs-sm);
  line-height: 1.35;
  color: #1f2937;
  flex: 1 1 auto;
  min-width: 0; /* allow shrinking so long names wrap instead of pushing time */
  overflow-wrap: anywhere; /* break long unbroken tokens cleanly */
}

.metrics-container {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  flex-shrink: 1;
  margin-left: auto;
}

.metric-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: var(--fs-xs);
  padding: 2px 7px;
  border-radius: 6px;
  line-height: 1.4;
}

.metric-pill.va {
  background: #f0fdf4;
  color: #15803d;
}

.metric-pill.nva {
  background: #fefce8;
  color: #a16207;
}

.metric-pill.total {
  background: #eff6ff;
  color: #1d4ed8;
}

.step-time {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: var(--fs-sm);
  color: #1f2937;
  margin-left: auto;
  line-height: 1.35; /* match .text so the baseline aligns with the first line */
}

.metric-label {
  font-weight: 600;
  font-size: var(--fs-xs);
  opacity: 0.75;
}

.metric-value {
  font-weight: 500;
}

/* Pills: pushed right with margin-left:auto so the metric/time block follows
   immediately. When absent, metric/time keeps its own margin-left:auto. */
.attr-pills {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: auto;
}

.attr-pill {
  flex-shrink: 0;
  font-size: var(--fs-xs);
  font-weight: 600;
  padding: 0 8px;
  border-radius: 999px;
  /* match .text / .step-time line-height so all three share the same baseline */
  line-height: 1.35;
  letter-spacing: 0.02em;
  text-transform: lowercase;
  border: 1px solid transparent;
  white-space: nowrap;
}
.attr-pill.optional {
  background: #eef2ff;
  color: #4338ca;
  border-color: #c7d2fe;
}
.attr-pill.negative {
  background: #fee2e2;
  color: #b91c1c;
  border-color: #fecaca;
}
.attr-pill.low-confidence {
  background: #fff7ed;
  color: #c2410c;
  border-color: #fed7aa;
}
</style>
